'use client';

import * as React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  SHEET_WIDTHS,
  HEADER_CLASSES,
  SPACING,
  ANIMATION_DURATION,
  type SheetWidth,
} from '@/components/shared/modal-sheet-constants';
import { cleanupPointerEvents } from '@/components/shared/utils/modalCleanup';

/**
 * BaseSheet Props Interface
 *
 * Unified interface for all sheet components in the application.
 * Provides consistent API on top of shadcn Sheet primitive.
 */
export interface BaseSheetProps {
  /**
   * Whether the sheet is open
   */
  open: boolean;

  /**
   * Callback when open state changes
   * Called when user closes sheet via X button, overlay click, or ESC key
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Sheet title
   * Displayed in header, used for aria-labelledby
   */
  title?: string;

  /**
   * Sheet description
   * Displayed below title, used for aria-describedby
   */
  description?: string;

  /**
   * Sheet width
   * @default 'lg'
   */
  width?: SheetWidth;

  /**
   * Sheet content
   */
  children: React.ReactNode;

  /**
   * Optional header content (rendered after title/description)
   * Useful for tabs, badges, or additional controls
   */
  headerContent?: React.ReactNode;

  /**
   * Optional footer content (buttons, actions)
   * Rendered at bottom of sheet
   */
  footer?: React.ReactNode;

  /**
   * Whether to show the close button in top-right
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Additional className for sheet content container
   */
  className?: string;

  /**
   * Additional className for the sheet body (scrollable area)
   */
  bodyClassName?: string;

  /**
   * Callback fired when sheet is closed (before onOpenChange)
   * Useful for cleanup operations
   */
  onClose?: () => void;

  /**
   * Side from which sheet slides in
   * @default 'right'
   */
  side?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * Prevent closing sheet by clicking overlay
   * @default false
   */
  disableOverlayClose?: boolean;

  /**
   * Prevent closing sheet with ESC key
   * @default false
   */
  disableEscapeClose?: boolean;

  /**
   * Prevent closing when nested modal/sheet is open
   * Pass a ref or state that tracks nested modal state
   * Based on audiences best practices for nested modal handling
   */
  nestedModalOpen?: boolean | React.RefObject<boolean>;

  /**
   * Callback for interact outside events
   * Useful for preventing parent sheet from closing when nested modal is open
   */
  onInteractOutside?: (event: Event) => void;
}

/**
 * BaseSheet - Unified sheet component for the application
 *
 * This component provides a consistent interface on top of shadcn Sheet primitive.
 * Use this for all sheet (slide-in panel) implementations to ensure consistency.
 *
 * Features:
 * - Standardized widths (narrow → full)
 * - Consistent prop interface
 * - Built-in header/footer patterns
 * - Scrollable body with fixed header/footer
 * - Accessibility attributes
 * - Keyboard navigation (ESC, Tab)
 * - Focus management
 *
 * @example Basic usage
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <BaseSheet
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Signal Details"
 *   description="View and manage signal information"
 *   width="xl"
 * >
 *   <SignalPanel signal={selectedSignal} />
 * </BaseSheet>
 * ```
 *
 * @example With header content and footer
 * ```tsx
 * <BaseSheet
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Task Details"
 *   width="lg"
 *   headerContent={
 *     <Tabs value={activeTab} onValueChange={setActiveTab}>
 *       <TabsList>
 *         <TabsTrigger value="details">Details</TabsTrigger>
 *         <TabsTrigger value="history">History</TabsTrigger>
 *       </TabsList>
 *     </Tabs>
 *   }
 *   footer={
 *     <div className="flex justify-end gap-2">
 *       <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
 *       <Button onClick={handleSave}>Save</Button>
 *     </div>
 *   }
 * >
 *   <TaskContent />
 * </BaseSheet>
 * ```
 */
export const BaseSheet = React.forwardRef<HTMLDivElement, BaseSheetProps>(
  (
    {
      open,
      onOpenChange,
      title,
      description,
      width = 'lg',
      children,
      headerContent,
      footer,
      showCloseButton = true,
      className,
      bodyClassName,
      onClose,
      side = 'right',
      disableOverlayClose = false,
      disableEscapeClose = false,
      nestedModalOpen = false,
      onInteractOutside,
    },
    ref
  ) => {
    // Production-safe cleanup: Remove stuck pointer-events when sheet closes
    // Based on audiences best practices - sheets have longer animation (300ms)
    React.useEffect(() => {
      if (!open) {
        // Delay cleanup to allow animation to complete (300ms animation + 50ms buffer)
        const timeoutId = setTimeout(() => {
          cleanupPointerEvents('BaseSheet', false);
        }, ANIMATION_DURATION.sheet + 50);
        return () => clearTimeout(timeoutId);
      }
    }, [open]);

    // Check if nested modal is open (supports both boolean and ref)
    const isNestedModalOpen = React.useMemo(() => {
      if (typeof nestedModalOpen === 'boolean') {
        return nestedModalOpen;
      }
      if (nestedModalOpen && 'current' in nestedModalOpen) {
        return nestedModalOpen.current;
      }
      return false;
    }, [nestedModalOpen]);

    // Handle sheet state change
    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        // Don't close if nested modal is open
        if (!newOpen && isNestedModalOpen) {
          return;
        }

        // Call onClose callback if sheet is closing
        if (!newOpen && onClose) {
          // Delay callback to prevent race condition with react-remove-scroll
          // Based on audiences best practices
          setTimeout(() => {
            onClose();
          }, ANIMATION_DURATION.sheet + 50);
        }
        onOpenChange(newOpen);
      },
      [onClose, onOpenChange, isNestedModalOpen]
    );

    // Handle overlay interaction
    const handleOverlayClick = React.useCallback(
      (e: Event) => {
        // Prevent closing if nested modal is open or overlay close is disabled
        if (isNestedModalOpen || disableOverlayClose) {
          e.preventDefault();
        }
        // Call custom handler if provided (for additional guards)
        if (onInteractOutside) {
          onInteractOutside(e);
        }
      },
      [disableOverlayClose, isNestedModalOpen, onInteractOutside]
    );

    // Handle escape key
    const handleEscapeKeyDown = React.useCallback(
      (e: Event) => {
        // Prevent closing if nested modal is open or escape close is disabled
        if (isNestedModalOpen || disableEscapeClose) {
          e.preventDefault();
        }
      },
      [disableEscapeClose, isNestedModalOpen]
    );

    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          ref={ref}
          side={side}
          className={cn(
            SHEET_WIDTHS[width],
            'h-full !p-0 !gap-0 flex flex-col',
            className
          )}
          hideCloseButton={!showCloseButton}
          onInteractOutside={handleOverlayClick}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          {/* Header */}
          {(title || description || headerContent) && (
            <div className={cn('flex-shrink-0 bg-white z-10', HEADER_CLASSES.sheet)}>
              {(title || description) && (
                <SheetHeader>
                  {title && <SheetTitle>{title}</SheetTitle>}
                  {description && <SheetDescription>{description}</SheetDescription>}
                </SheetHeader>
              )}
              {headerContent && (
                <div className="mt-4">
                  {headerContent}
                </div>
              )}
            </div>
          )}

          {/* Body - Children manage their own layout and scrolling */}
          <div className={cn('flex-1 min-h-0', bodyClassName)}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className={cn('flex-shrink-0 bg-white border-t border-slate-200', SPACING.sheetPadding)}>
              {footer}
            </div>
          )}
        </SheetContent>
      </Sheet>
    );
  }
);

BaseSheet.displayName = 'BaseSheet';

